import { useState } from "react";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { trackGuestCheckoutSignIn } from "@/lib/analytics-enhanced";
import { secondsUntilResend } from "@/lib/auth-resend";

interface GuestCheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Shown on return from Stripe after paying without being signed in.
 *
 * The webhook has attached Lite to the account for the email used at checkout
 * (creating it if there was none) and emailed a sign-in link. This browser is
 * deliberately *not* signed in automatically: that would let anyone who pays
 * with someone else's email into that person's account. So the page says what
 * to do next, offers Google for an address that is a Google account, and can
 * send the link again.
 */
export function GuestCheckoutDialog({ open, onOpenChange }: GuestCheckoutDialogProps) {
  const { signInWithGoogle } = useAuthContext();
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "wait" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  const resend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState("sending");
    // `shouldCreateUser: false`: the account is the webhook's to make, for the
    // email Stripe collected. A typo here must not create a second one.
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}/?checkout=signed_in` },
    });
    trackGuestCheckoutSignIn("link_resend", { ok: error ? 0 : 1 });
    // The webhook sent a link moments ago, so Supabase's once-a-minute limit
    // is the expected answer to an early press, not a failure.
    const wait = error ? secondsUntilResend(error.message) : null;
    if (wait !== null) {
      setState("wait");
      setMessage(`We just sent one, so check your inbox. You can send another in ${wait} seconds.`);
      return;
    }
    if (error) {
      setState("error");
      setMessage(
        /signups not allowed|not found/i.test(error.message)
          ? "We don't have an account for that address yet. If you just paid, give it a minute and try again — or use the exact email you entered at checkout."
          : error.message,
      );
      return;
    }
    setState("sent");
    setMessage(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <CheckCircle2 className="h-6 w-6 text-primary" />
            Payment received
          </DialogTitle>
          <DialogDescription className="text-foreground">
            Lite is on your account. To use it here, sign in with the email you entered at checkout.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>
              <span className="font-medium">Check your email.</span> We sent a link that signs you in. It can take a
              minute; check spam if it doesn&apos;t show up.
            </span>
          </p>

          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              trackGuestCheckoutSignIn("google");
              void signInWithGoogle("/?checkout=signed_in");
            }}
          >
            Or continue with Google
          </Button>
          <p className="-mt-1 text-center text-xs text-muted-foreground">
            Works if that email is a Google account.
          </p>

          <form onSubmit={resend} className="space-y-2 border-t border-border pt-3">
            <label htmlFor="guest-checkout-email" className="text-xs font-medium text-muted-foreground">
              Didn&apos;t get the link? Send it again
            </label>
            <div className="flex gap-2">
              <Input
                id="guest-checkout-email"
                type="email"
                autoComplete="email"
                placeholder="Email you paid with"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" variant="secondary" disabled={state === "sending" || !email.trim()}>
                {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
              </Button>
            </div>
            {state === "sent" && <p className="text-xs text-primary">Sent. Use the newest link if you get more than one.</p>}
            {state === "wait" && message && <p className="text-xs text-muted-foreground">{message}</p>}
            {state === "error" && message && <p className="text-xs text-destructive">{message}</p>}
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Trouble? Email{" "}
            <a href="mailto:cole@wisconsinpropertymap.com" className="font-medium text-primary hover:underline">
              cole@wisconsinpropertymap.com
            </a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
